/**
 * Display URLs for `sitescope-attachment-pending://` images so the author sees
 * the photo before the request exists. Web uses blob: object URLs; native uses
 * data URIs. Form HTML always stores the pending ref, never the preview URL.
 */
import { isLocalNetworkImgSrc } from '../utils/richContent/localNetworkImgSrc';

const previewByPendingRef = new Map<string, string>();
const pendingRefByPreviewSrc = new Map<string, string>();

export function rememberPendingInlineImagePreview(
  pendingRef: string,
  previewSrc: string,
): void {
  const previous = previewByPendingRef.get(pendingRef);
  if (previous && previous !== previewSrc) {
    // Editors can still contain the earlier full-size data/blob preview while
    // view hydration replaces the preferred display source with a thumbnail.
    // Keep the old reverse alias until the session ends so save can always
    // serialize it back to the same durable attachment reference. Revoking or
    // deleting it here leaks embedded image bytes into persisted HTML.
    pendingRefByPreviewSrc.set(previous, pendingRef);
  }
  previewByPendingRef.set(pendingRef, previewSrc);
  pendingRefByPreviewSrc.set(previewSrc, pendingRef);
}

export function pendingInlineImagePreviewSrc(src: string): string {
  const trimmed = src.trim();
  const normalized = trimmed.replace(/\/$/, '');
  return (
    previewByPendingRef.get(trimmed) ??
    previewByPendingRef.get(normalized) ??
    trimmed
  );
}

export function isDisplayableInlineImageSrc(src: string): boolean {
  return /^(data:image\/|blob:|file:|https?:)/i.test(src.trim());
}

/** Tiptap parseHTML reads this after `parkImgSrcsForDomParse` strips `src`. */
export const SS_ATTACHMENT_SRC_ATTR = 'data-ss-attachment-src';

/**
 * Move custom-scheme / local-network img URLs off `src` before Tiptap
 * `DOMParser` / contenteditable sees them. Chrome 142 treats
 * `sitescope-attachment://9` as local IPv4 `0.0.0.9` and prompts for
 * local-network access on public origins (DEVSECURE web).
 */
export function parkImgSrcsForDomParse(html: string): string {
  return html.replace(
    /(<img\b[^>]*?)\ssrc=(["'])([^"']+)\2/gi,
    (full, beforeSrc: string, quote: string, src: string) => {
      // ⚠️ A `data:` IMAGE IS PARKED TOO (2026-10-01, found on the #1530 walk).
      // The image node's own rule is `img[src]:not([src^="data:"])`, so a
      // pasted `<img src="data:image/...">` left on `src` parses to NOTHING:
      // with text around it the picture silently vanished, and when the
      // clipboard carried only the image Tiptap's `insertContent` fell back
      // to inserting the 6 KB source STRING as text. Parked, it parses through
      // the `data-ss-attachment-src` rule, the node view paints it, and the
      // rehost pass turns it into an upload exactly as designed; emission is
      // already held back while a `data:` src is in the document.
      if (/^data:image\//i.test(src.trim())) {
        return `${beforeSrc} ${SS_ATTACHMENT_SRC_ATTR}=${quote}${src}${quote}`;
      }
      const preview = pendingInlineImagePreviewSrc(src);
      if (
        isDisplayableInlineImageSrc(preview) &&
        !isLocalNetworkImgSrc(preview)
      ) {
        return full;
      }
      if (
        /^sitescope-attachment(?:-pending)?:/i.test(src.trim()) ||
        isLocalNetworkImgSrc(src)
      ) {
        return `${beforeSrc} ${SS_ATTACHMENT_SRC_ATTR}=${quote}${src}${quote}`;
      }
      return full;
    },
  );
}

/**
 * Put parked refs back on `src` in the HTML string (not via `img.src =`).
 * Assigning `img.src` on a detached node still makes Chrome GET the URL.
 */
export function unparkImgSrcsAfterDomParse(html: string): string {
  return html.replace(
    /(<img\b[^>]*?)\sdata-ss-attachment-src=(["'])([^"']+)\2/gi,
    (full, beforeAttr: string, quote: string, src: string) => {
      if (/\ssrc=/i.test(full)) {
        return full;
      }
      return `${beforeAttr} src=${quote}${src}${quote}`;
    },
  );
}

/** Park before `innerHTML =` so Chrome does not GET custom-scheme hosts. */
export function assignDetachedInnerHTML(element: Element, html: string): void {
  element.innerHTML = parkImgSrcsForDomParse(html);
}

export function serializeDetachedInnerHTML(element: Element): string {
  return unparkImgSrcsAfterDomParse(element.innerHTML);
}

/** Swap stored attachment refs to preview URLs so native Image/WebView can load them. */
export function rewriteHtmlAttachmentRefsToPreviewSrcs(html: string): string {
  return html.replace(
    /(<img\b[^>]*\bsrc=)(["'])(sitescope-attachment(?:-pending)?:\/\/[^"']+)\2/gi,
    (full, prefix: string, quote: string, src: string) => {
      const preview = pendingInlineImagePreviewSrc(src);
      return preview !== src && isDisplayableInlineImageSrc(preview)
        ? `${prefix}${quote}${preview}${quote}`
        : full;
    },
  );
}

function decodeHtmlAttr(value: string): string {
  return value
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16)),
    );
}

function pendingRefForPreviewSrc(src: string): string | undefined {
  const decoded = decodeHtmlAttr(src);
  const mapped =
    pendingRefByPreviewSrc.get(src) ?? pendingRefByPreviewSrc.get(decoded);
  if (mapped) {
    return mapped;
  }
  try {
    return pendingRefByPreviewSrc.get(decodeURIComponent(decoded));
  } catch {
    return undefined;
  }
}

/** Swap in-editor preview `src` values back to pending refs before form persist. */
export function rewriteHtmlPreviewSrcsToPendingRefs(html: string): string {
  let next = html;
  for (const [previewSrc, pendingRef] of pendingRefByPreviewSrc) {
    if (previewSrc && next.includes(previewSrc)) {
      next = next.split(previewSrc).join(pendingRef);
    }
  }
  // 10tap may HTML-escape the data URI so the exact-map replace above misses.
  return next.replace(
    /(<img\b[^>]*\bsrc=)(["'])(data:image\/[^"']+)\2/gi,
    (full, prefix: string, quote: string, src: string) => {
      const pending = pendingRefForPreviewSrc(src);
      return pending ? `${prefix}${quote}${pending}${quote}` : full;
    },
  );
}

/** Keep one node when a native bridge echoes the same inserted attachment. */
export function dedupeInlineImageReference(
  html: string,
  reference: string,
): string {
  let seen = false;
  return html.replace(/<img\b[^>]*>/gi, tag => {
    const src = /\bsrc=(['"])([^'"]+)\1/i.exec(tag)?.[2];
    if (src !== reference) {
      return tag;
    }
    if (seen) {
      return '';
    }
    seen = true;
    return tag;
  });
}

export function insertPendingImageHtml(
  html: string,
  pendingRef: string,
  alt = 'attachment',
): string {
  if (html.includes(pendingRef)) {
    return html;
  }
  const tag = `<img src="${pendingRef}" alt="${alt}">`;
  const trimmed = html.trim();
  if (!trimmed || trimmed === '<p></p>' || trimmed === '<p><br></p>') {
    return `<p>${tag}</p>`;
  }
  if (trimmed.endsWith('</p>')) {
    return `${trimmed.slice(0, -4)}${tag}</p>`;
  }
  return `${trimmed}${tag}`;
}
